import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { intlLocale } from "@/i18n";

export type DateFormat = "date" | "datetime" | "relative";

const TIME_ZONE = "Asia/Tokyo";

function toDate(value: string | Date | number): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function jstParts(date: Date): Record<string, string> {
  // en-US numerals + gregorian calendar regardless of UI language (th-TH defaults to Buddhist years)
  const out: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)) {
    out[part.type] = part.value;
  }
  return out;
}

/** `2026-09-24` (JST calendar date). */
export function formatDateJST(value: string | Date | number): string {
  const date = toDate(value);
  if (!date) {
    return "";
  }
  const p = jstParts(date);
  return `${p.year ?? ""}-${p.month ?? ""}-${p.day ?? ""}`;
}

/** `2026-09-24 10:00 JST`. */
export function formatDateTimeJST(value: string | Date | number): string {
  const date = toDate(value);
  if (!date) {
    return "";
  }
  const p = jstParts(date);
  return `${p.year ?? ""}-${p.month ?? ""}-${p.day ?? ""} ${p.hour ?? ""}:${p.minute ?? ""} JST`;
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "3 minutes ago" / "3 นาทีที่แล้ว" / "3 分前" (Intl.RelativeTimeFormat). */
export function formatRelative(
  value: string | Date | number,
  language?: string,
  now = Date.now(),
): string {
  const date = toDate(value);
  if (!date) {
    return "";
  }
  const seconds = Math.round((date.getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(intlLocale(language), { numeric: "auto" });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) {
      return rtf.format(Math.trunc(seconds / size), unit);
    }
  }
  return rtf.format(0, "minute");
}

/** Re-render relative times every 30 s so "just now" becomes "1 minute ago" by itself. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) {
      return;
    }
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

export interface DateJSTProps {
  value: string | Date | number | null | undefined;
  format?: DateFormat;
  className?: string;
}

/** Every date in the admin is shown in Japan time (the shop's legal time zone). */
export function DateJST({ value, format = "datetime", className }: DateJSTProps) {
  const { i18n } = useTranslation();
  const now = useNow(format === "relative");
  if (value === null || value === undefined) {
    return null;
  }
  const date = toDate(value);
  if (!date) {
    return null;
  }
  const absolute = formatDateTimeJST(date);
  const text =
    format === "date"
      ? formatDateJST(date)
      : format === "relative"
        ? formatRelative(date, i18n.resolvedLanguage, now)
        : absolute;
  return (
    <time
      dateTime={date.toISOString()}
      title={format === "datetime" ? undefined : absolute}
      className={className}
    >
      {text}
    </time>
  );
}
